/**
 * iter-2026 — Client-side cache for the central role registry.
 *
 * Every surface that renders a role dropdown or badge should read
 * through this helper instead of a hardcoded list. The hook caches
 * the response in memory for the current page life so dropdowns stay
 * snappy, with a `refresh()` for post-mutation refetches.
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

let _cache = null;
const _subs = new Set();

async function _load() {
    const r = await api.get("/roles");
    _cache = r.data.roles || [];
    _subs.forEach((cb) => cb(_cache));
    return _cache;
}

export function useRoleRegistry() {
    const [roles, setRoles] = useState(_cache);
    useEffect(() => {
        _subs.add(setRoles);
        if (_cache === null) _load().catch(() => setRoles([]));
        return () => _subs.delete(setRoles);
    }, []);
    const refresh = useCallback(() => _load().catch(() => {}), []);
    return { roles: roles || [], refresh };
}

/**
 * Fetch roles that may be assigned to a NEW user for `orgId`. Returns
 * both the active role codes (for dropdowns) and the full role rows
 * (so callers can show descriptions / badges).
 */
export async function fetchAssignableRoles(orgId) {
    const r = await api.get("/roles/assignable", { params: orgId ? { org_id: orgId } : {} });
    return r.data.roles || [];
}
